/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import React from 'react'
import ReactDOM from 'react-dom'
import jwt_decoder from 'jwt-decode'

import Environment from '../js/utilities/Environment'
import AppState from '../js/utilities/AppState'
import IdentitySDK from '../js/utilities/IdentitySDK'

// DEBUG: Attach to window when running locally
if (process.env.VSPACES_DEBUG) {
    window.Environment = Environment
}

/**
 * Shows the landing screen for the app.
 */
export default class LandingPage extends React.PureComponent {

    /** @type {HTMLDivElement} Element that holds the component */
    static rootDiv = null

    /** Show the landing screen */
    static show() {
        this.rootDiv = document.createElement('div')
        this.rootDiv.setAttribute('id', 'landing-page')
        this.rootDiv.setAttribute('style', 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 99999; background-color: #202020; opacity: 1;')
        document.body.appendChild(this.rootDiv)

        // Cleanup
        const close = () => {
            this.rootDiv.style.transition = 'opacity 0.5s ease'
            this.rootDiv.style.opacity = 0

            setTimeout(() => {
                ReactDOM.unmountComponentAtNode(this.rootDiv)
                this.rootDiv.remove()
                this.rootDiv = null
            }, 500)
        }

        // Render
        ReactDOM.render(<LandingPage onClose={close} />, this.rootDiv)
    }

    /** Base URL for this space */
    hostURL = `https://${Environment.current.data.domains[0]}`

    /** Core app that will be used to start everything */
    app = null

    /** Details about the space we are entering */
    details = null

    /** Information about the current user */
    user = null

    /** @type {Error} Error that has occurred */
    error = null

    /** Called on load */
    async componentDidMount() {
        this.updateTimer = setInterval(() => this.forceUpdate(), 1000)

        let search = new URLSearchParams(window.location.search)
        AppState.isAllowLeave = search.get('allowLeave') === '1'

        // Determine if the given URL is valid
        const success = this.isCorrectURL(search)
        if (success === null) {
            return
        }

        // Only log user in if we have been given a correct URL
        if (success) {
            await this.performLogin(search)
        }

        if (this.details) {
            // For an event, determine if it is currently happening
            if (this.details.isEvent) {
                const startAt = new Date(this.details.startAt).getTime()
                const endAt = this.details.endAt ? new Date(this.details.endAt).getTime() : null

                // Event is currently running
                if (startAt < Date.now() && (!endAt || endAt > Date.now())) {
                    window.preventLeavePopup = true
                    window.open(`https://${Environment.current.data.domains[0]}/@${this.details.spaceAlias || this.details.spaceId}?from_event=${this.details.id}`, '_self')
                    return
                }
            }

            // Attach information to app state
            AppState.serverID = this.details.isEvent ? this.details.spaceId : this.details.id
            AppState.businessID = this.details.isEvent ? this.details.spaceBusinessId : this.details.businessId
            AppState.dimensionID = search.get('d') || 'default'

            // Construct new tab title
            const tabTitle = this.details.isEvent && this.details.displayName ? `${this.details.displayName} | ` : (this.details.alias == null ? '' : `@${this.details.alias} | `)
            document.title = `${tabTitle}Vatom Spaces`
        } else {
            document.title = 'Something Went Wrong | Vatom Spaces'
        }

        // Import three.js
        window.THREE = await import('three') || window.THREE

        // Import app
        this.app = await import('../js/index')

        // Start loading app
        try {
            await this.app.start(this.details, this.onEnter, this.user, this.error)
        } catch (err) {
            this.app.showError(err)
        }
    }

    /**
     * Attempts to log the user in using the SDK.
     * @param {URLSearchParams} search URL search parameters.
     */
    async performLogin(search) {
        try {

            this.user = await IdentitySDK.initialLogin(search)

            // Decode user access token
            const accessToken = await IdentitySDK.getAccessToken()
            const decoded = jwt_decoder(accessToken)
            this.user.region = decoded['urn:vatominc:region'] || ''

            // Fetch space details
            await this.getDetails(search)

        } catch (err) {

            // Token refresh failed, so delete the token and try again
            if (err.message && err.message.includes('token refresh failed')) {
                console.warn('[LandingPage] Token refresh failed, removing token and trying again...')
                IdentitySDK.setTokenObject(null)
                await this.performLogin(search)
                return
            }

            console.error('[LandingPage] Failed to perform login for user.', err)
            this.error = err

        }
    }

    /**
     * Determines if the given gather URL is correct.
     * @param {string} url URL to check.
     * @param {string} protocol HTTP protocol used.
     * @returns {boolean} `true` if the gather URL is correct, `false` otherwise.
     */
    isCorrectGatherURL(url, protocol) {
        const paths = url.split('/').filter(p => !!p)

        // Not enough paths given
        if (paths.length < 3) {
            this.error = new Error('It appears that the event ID is missing from the URL. Please double-check the URL you entered and ensure that you have correctly typed the URL for this event.')
            this.error.title = "Oops! We couldn't find the event you're looking for."
            this.error.buttons = {
                primary: { hidden: true }
            }
            return false
        }

        const branchIdx = paths.findIndex(p => p.includes('v='))

        // Gather URL is correct, no branch given (e.g. https://spaces.vatom.com/gather/2j5ldn65kdh34)
        if (paths.length === 3 && branchIdx < 0) {
            return true
        }

        // Gather URL is correct, branch given (e.g. https://spaces.vatom.com/branch/gather/2j5ldn65kdh34)
        if (paths.length === 4 && branchIdx < 0) {
            return true
        }

        // URL is given as https://spaces.vatom.com/gather/2j5ldn65kdh34?v=branch
        // and should be redirected to https://spaces.vatom.com/branch/gather/2j5ldn65kdh34

        const type = paths[paths.length - 2]
        const domain = paths[0]
        const branch = branchIdx >= 0 ? paths[branchIdx].split('v=')[1] : (paths.length - 3 === 0 ? null : paths[paths.length - 3])

        let eventId = paths[paths.length - 1]
        const queryParamIdx = eventId.indexOf('?')
        if (queryParamIdx >= 0) {
            eventId = eventId.slice(0, queryParamIdx)
        }

        // Redirect to correct URL
        window.location.href = `${protocol}://${domain}/${branch ? branch + '/' : ''}${type}/${eventId}`
        return null
    }

    /**
     * Determines if the given URL is correct.
     * @param {URLSearchParams} search Search parameters.
     * @returns {boolean} `true` if the given URL is correct, `false` otherwise.
     */
    isCorrectURL(search) {
        const paths = window.location.pathname.split('/').filter(p => !!p)

        // No full URL given
        if (paths.length < 1 && window.location.hostname !== 'localhost') {
            this.error = new Error(`Please verify the URL for any errors and consider copying and pasting it instead. If the issue persists, please ensure that the URL begins with "${this.hostURL}".`)
            this.error.title = 'Page Not Found'
            this.error.buttons = {
                primary: { text: 'Go Back', action: () => window.location.reload() },
                secondary: { text: 'Visit the Home Page', action: () => window.open(this.hostURL, '_self') }
            }
            return false
        }

        const windowURL = window.location.href.split('://')
        const protocol = windowURL[0]
        const url = windowURL[1]
        const domain = url.indexOf('/') >= 0 ? url.slice(0, url.indexOf('/')) : url

        // Trying to use event URL with space alias
        if (url.includes('/gather/') && url.includes('@')) {
            this.error = new Error('It appears that the event ID is missing from the URL. Please double-check the URL you entered and ensure that you have correctly typed the URL for this event.')
            this.error.title = "Oops! We couldn't find the event you're looking for."
            this.error.buttons = {
                primary: { hidden: true }
            }
            return false
        }

        // Check event format
        if (url.includes('/gather/')) {
            return this.isCorrectGatherURL(url, protocol)
        }

        const branch = search.get('v') || null

        // Special case for localhost
        if (url.includes('localhost')) {

            // We are already on the correct URL, such as http://localhost:80/?id=space
            if (domain.includes('localhost')) {
                return true
            }

            const last = paths[paths.length - 1]
            const alias = last.split('?')[0].slice(1)

            // Remove branch name
            search.delete('v')
            const extraQueryParams = search.size > 0 ? `&${search.toString()}` : ''

            // Using a URL such as https://spaces.vatom.com/localhost/@space
            if (paths.findIndex(p => p === 'localhost') >= 0) {
                window.location.href = `http://localhost:80/?id=${alias}${extraQueryParams}`
                return null
            }

            // Using a branched URL such as https://spaces.vatom.com/@space?v=localhost
            if (branch === 'localhost') {
                window.location.href = `http://localhost:80/?id=${alias}${extraQueryParams}`
                return null
            }

        }

        // Using correct space format, such as https://spaces.vatom.com/@space
        // or https://spaces.vatom.com/branch/@space
        if (url.includes('/@') && url.indexOf('v=') < 0) {
            return true
        }

        // Not using a space URL
        if (!url.includes('/@')) {
            this.error = new Error('It seems that the Space ID is missing from the URL. Please double-check the URL you entered and ensure that you have correctly typed the URL for the space. ')
            this.error.title = "Oops! We couldn't find the space you're looking for."
            this.error.buttons = {
                primary: { hidden: true }
            }
            return false
        }

        // URL is given as https://spaces.vatom.com/@space?v=branch and should be
        // redirected to https://spaces.vatom.com/branch/@space

        const alias = url.split('/@')[1].split('?')[0]

        // Remove branch name
        search.delete('v')
        const extraQueryParams = search.size > 0 ? `?${search.toString()}` : ''

        // Redirect to correct URL
        window.location.href = `${protocol}://${domain}/${branch ? branch + '/' : ''}@${alias}${extraQueryParams}`
        return null
    }

    /**
     * Retrieves the space details (at this stage, URL looks something
     * like: https://spaces.vatom.com/dev/?id=jdhjacsajcn).
     * @param {URLSearchParams} search URL search parameters.
     */
    getDetails = async search => {
        // Event URL
        if (window.location.href.includes('/gather/')) {
            const paths = window.location.pathname.split('/').filter(p => !!p)
            if (paths.length < 2) {
                this.error = new Error('No event ID exists.')
                return
            }

            const eventId = paths[paths.length - 1]
            try {
                let response = await fetch(`${Environment.current.data.servers.events}/events/${eventId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })

                // Unsucessful response
                if (!response || !response.ok) {
                    const err = new Error(`Please verify the URL for any errors and consider copying and pasting it instead. If the issue persists, please ensure that the URL begins with "${this.hostURL}/gather".`)
                    err.title = 'Page Not Found'
                    err.extra = `Received ${response.status} ${response.statusText} when fetching event information.`
                    err.buttons = {
                        primary: { text: 'Go Back', action: () => window.location.reload() },
                        secondary: { text: 'Visit the Home Page', action: () => window.open(this.hostURL, '_self') }
                    }
                    throw err
                }

                this.details = await response.json()

                // No event details have been returned
                if (!this.details || typeof this.details != 'object' || Object.keys(this.details).length < 1) {
                    throw new Error('Failed to find details about the event. Please try again later.')
                }

                // Flag this as an event
                this.details.isEvent = true

                // Fetch information about the current space
                response = await fetch(`${Environment.current.data.servers.events}/spaces/${this.details.spaceId}`, {
                    method: 'GET',
                })

                // Unsucessful response
                if (!response || !response.ok) {
                    const err = new Error(`Please verify the URL for any errors and consider copying and pasting it instead. If the issue persists, please ensure that the URL begins with "${this.hostURL}/gather".`)
                    err.title = 'Page Not Found'
                    err.extra = `Received ${response.status} ${response.statusText} when fetching space information.`
                    err.buttons = {
                        primary: { text: 'Go Back', action: () => window.location.reload() },
                        secondary: { text: 'Visit the Home Page', action: () => window.open(this.hostURL, '_self') }
                    }
                    throw err
                }

                const additional = await response.json()

                // No space details have been returned
                if (!additional || typeof additional != 'object' || Object.keys(additional).length < 1) {
                    throw new Error('Failed to find details about the space. Please try again later.')
                }

                // Attach additional fields
                this.details.spaceCreatedById = additional.createdById
                this.details.spaceBusinessId = additional.businessId

                // Allow other files to access these details
                AppState.spaceDetails = { ...this.details, config: additional.config || null }
            } catch (err) {
                this.error = err
            }

            return
        }

        // Space URL
        const paths = window.location.pathname.split('/@').filter(p => !!p)
        const last = paths[paths.length - 1]
        const alias = search.get('id') || last.split('?')[0]
        if (!alias) {
            this.error = new Error('No space ID exists.')
            return
        }

        try {
            const response = await fetch(`${Environment.current.data.servers.events}/spaces/${alias}`, {
                method: 'GET',
            })

            // Unsuccessful response
            if (!response || !response.ok) {
                const err = new Error(`Please verify the URL for any errors and consider copying and pasting it instead. If the issue persists, please ensure that the URL begins with "${this.hostURL}".`)
                err.title = 'Page Not Found'
                err.extra = `Received ${response.status} ${response.statusText} when fetching space information.`
                err.buttons = {
                    primary: { text: 'Go Back', action: () => window.location.reload() },
                    secondary: { text: 'Visit the Home Page', action: () => window.open(this.hostURL, '_self') }
                }
                throw err
            }

            this.details = await response.json()

            // No space details have been returned
            if (!this.details || typeof this.details != 'object' || Object.keys(this.details).length < 1) {
                throw new Error('Failed to find details about the space. Please try again later.')
            }

            // Allow other files to access these details
            AppState.spaceDetails = { ...this.details }

            // Check if we have been redirected here from an event
            AppState.redirectFromEvent = search.get('from_event') ? { id: search.get('from_event') } : null
        } catch (err) {
            if (err.message === 'Failed to fetch') {
                const error = new Error('Failed to find details about the space. Please try again later.')
                error.extra = `An unexpected error occurred when fetching from endpoint "${Environment.current.data.servers.events}/spaces/${alias}".`
                this.error = error
                return
            }

            this.error = err
        }
    }

    /** Called when the user enters the space */
    onEnter = () => {
        this.props.onClose()
    }

    /** Render UI */
    render() {
        return <></>
    }

}
